import { useEffect, useId } from 'react';
import { track } from '@/lib/analytics';
import {
  paywallEventProps,
  subscribePath,
  type PaywallSource,
} from '@/lib/paywall';
import { useLearnerAuth } from '@/providers/LearnerAuth';

export type SubscriptionGateProps = {
  source: PaywallSource;
  message: string;
  content?: string;
  heroEmoji?: string;
  stickerImageUrls?: string[];
  onDismiss: () => void;
  /** Full-page replacement (hard gates) or overlay (finish-screen / pack tap). */
  variant?: 'page' | 'modal';
  open?: boolean;
};

/**
 * Compact subscription gate — same chrome as mobile `SubscriptionGateSheet`.
 * Title stays "Premium members only"; message / hero follow the trigger source.
 * Checkout still happens on `/app/subscribe` (Stripe), with source forwarded.
 */
export function SubscriptionGate({
  source,
  message,
  content,
  heroEmoji,
  stickerImageUrls = [],
  onDismiss,
  variant = 'page',
  open = true,
}: SubscriptionGateProps) {
  const titleId = useId();
  const auth = useLearnerAuth();
  const analytics = {
    source,
    surface: 'gate' as const,
    content,
    signed_in: Boolean(auth?.isSignedIn),
  };

  useEffect(() => {
    if (!open) return;
    track('Paywall Gate Shown', paywallEventProps(analytics));
    // Fire once per open for a given source/content — not on signed_in flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional
  }, [open, source, content]);

  if (!open) return null;

  const dismiss = () => {
    track('Paywall Gate Dismiss Press', paywallEventProps(analytics));
    onDismiss();
  };

  const card = (
    <div
      role="dialog"
      aria-modal={variant === 'modal' ? true : undefined}
      aria-labelledby={titleId}
      className="w-full max-w-[360px] rounded-3xl bg-[#F3FBF0] px-6 pb-5 pt-7 text-center shadow-xl"
      onClick={variant === 'modal' ? (event) => event.stopPropagation() : undefined}
    >
      <GateHero imageUrls={stickerImageUrls} emoji={heroEmoji} />
      <h1 id={titleId} className="font-nunito text-[22px] font-bold text-[#1E1E1E]">
        Premium members only
      </h1>
      <p className="mt-2 font-nunito text-[15px] leading-[22px] text-[#1E1E1E]/85">{message}</p>
      <a
        href={subscribePath(source, content)}
        className="mt-5 flex min-h-[52px] w-full items-center justify-center rounded-[14px] bg-[#399918] font-nunito text-[17px] font-bold text-white"
      >
        Get Hmonglus Premium
      </a>
      <button
        type="button"
        onClick={dismiss}
        className="mt-3.5 font-nunito text-[15px] font-bold text-[#5C6B5A]"
      >
        Not now
      </button>
    </div>
  );

  if (variant === 'modal') {
    return (
      <div
        className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4"
        role="presentation"
        onClick={dismiss}
      >
        {card}
      </div>
    );
  }

  return (
    <main className="grid min-h-[75vh] place-items-center p-6">
      {card}
    </main>
  );
}

function GateHero({ imageUrls, emoji }: { imageUrls: string[]; emoji?: string }) {
  if (imageUrls.length > 0) {
    return (
      <div className="mb-4 flex justify-center -space-x-3 overflow-hidden px-2">
        {imageUrls.slice(0, 5).map((url) => (
          <img
            key={url}
            src={url}
            alt=""
            className="h-16 w-16 rounded-2xl border-2 border-white bg-white object-contain shadow-sm"
          />
        ))}
      </div>
    );
  }
  if (emoji) {
    return (
      <div className="mb-3 flex justify-center">
        <span className="grid h-24 w-24 place-items-center rounded-full bg-white text-5xl shadow-sm">
          {emoji}
        </span>
      </div>
    );
  }
  return null;
}
